import { Injectable } from '@angular/core';
import { BehaviorSubject, Subscription } from 'rxjs';
import { MidiPlayerService } from './midi-player.service';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class PresentationAutoScrollService {

  private midiEventSubscription: Subscription | null = null;
  private lastTriggeredKey: string | null = null;

  enabled = false;
  active = false;
  mode: 'manual' | 'autoscroll' | 'karaoke' = 'manual';

  // Expose mode changes so components can react (e.g., clear highlighting when leaving karaoke)
  private modeSubject = new BehaviorSubject<'manual' | 'autoscroll' | 'karaoke'>(this.mode);
  mode$ = this.modeSubject.asObservable();

  // Callback to trigger slide navigation
  private navigateCallback: (() => void) | null = null;

  constructor(
    private midiPlayerService: MidiPlayerService,
    private settingsService: SettingsService
  ) {}

  setMode(mode: 'manual' | 'autoscroll' | 'karaoke'): void {
    this.mode = mode;
    this.enabled = mode !== 'manual';
    this.modeSubject.next(this.mode);
  }

  setNavigateCallback(callback: () => void): void {
    this.navigateCallback = callback;
  }

  startAutoScroll(): void {
    this.enabled = true;
    this.active = true;
    this.lastTriggeredKey = null;

    if (this.midiEventSubscription) {
      this.midiEventSubscription.unsubscribe();
    }

    // Subscribe to MIDI events to track when lyrics are triggered
    this.midiEventSubscription = this.midiPlayerService.event$.subscribe(ev => {
      if (!ev || !this.active || !this.enabled) return;

      if (ev.type === 'note' && ev.state === 'on') {
        const key = `t:${ev.track}:${ev.index}`;
        this.lastTriggeredKey = key;
        // For both autoscroll and karaoke modes, we'll track the last triggered key
        // The presenter component will check if it's the last visible key and navigate
      } else if (ev.type === 'status') {
        const s = ev.status;
        if (s === 'stopped' || s === 'ended') {
          this.lastTriggeredKey = null;
        }
      }
    });
  }

  stopAutoScroll(): void {
    this.active = false;
    this.lastTriggeredKey = null;

    if (this.midiEventSubscription) {
      this.midiEventSubscription.unsubscribe();
      this.midiEventSubscription = null;
    }
  }

  getLastTriggeredKey(): string | null {
    return this.lastTriggeredKey;
  }

  shouldNavigateNext(lastVisibleKey: string): boolean {
    if (!this.active || !this.enabled) return false;
    if (!this.lastTriggeredKey) return false;
    return this.lastTriggeredKey === lastVisibleKey;
  }
}
